/**
 * The Profile panel.
 *
 * Two halves, deliberately shown side by side:
 * - what you STORED (the editable form), and
 * - what the agent is ACTUALLY TOLD (the effective view), with the rung each
 *   value came from.
 *
 * They differ on purpose. `role_title` can come from the org chart you never
 * typed; `timezone` can come from the browser you are sitting at rather than
 * the one you saved. Showing only the stored half would leave a user unable to
 * explain what the agent knows about them — which is the whole point of an
 * "explicit, defined precedence, never a silent fallback".
 *
 * Presentational: the container owns the SDK and the save.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Badge } from '@localagent/ui/components/badge';
import { Button } from '@localagent/ui/components/button';
import { Input } from '@localagent/ui/components/input';
import { Label } from '@localagent/ui/components/label';
import type {
  UserProfile,
  UserProfileEffective,
  UserProfilePatch,
} from '@localagent/sdk';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';

/** Server-side rune caps (`profile_models.go`). Counted as code points. */
const CAPS = {
  preferred_name: 200,
  pronouns: 100,
  role_title: 200,
  timezone: 100,
  expertise: 500,
  working_hours: 200,
  bio: 2000,
} as const;

type ProfileField = keyof typeof CAPS;

const FIELDS: { key: ProfileField; label: string; placeholder: string }[] = [
  { key: 'preferred_name', label: 'Preferred name', placeholder: 'What should the agent call you?' },
  { key: 'pronouns', label: 'Pronouns', placeholder: 'they/them' },
  { key: 'role_title', label: 'Role / title', placeholder: 'Overrides your org-chart title' },
  { key: 'timezone', label: 'Timezone', placeholder: 'America/New_York' },
  { key: 'expertise', label: 'Expertise', placeholder: 'Domains you work in' },
  { key: 'working_hours', label: 'Working hours', placeholder: '09:00–17:00 weekdays' },
  { key: 'bio', label: 'About you', placeholder: 'A short bio the agent can use for context' },
];

function runeLength(value: string): number {
  return [...value].length;
}

/** Human wording for each precedence rung, so a value is never unattributed. */
const SOURCE_LABEL: Record<string, string> = {
  preferred_name: 'from your profile',
  email: 'from your email address',
  profile: 'from your profile',
  orgchart: 'from the org chart',
  client: 'from this session',
};

function EffectiveRow({
  label,
  value,
  source,
  testId,
}: {
  label: string;
  value: string | undefined;
  source?: string;
  testId: string;
}) {
  // Omit-when-unknown: an unresolved field shows an explicit "not set",
  // never a fabricated value, a placeholder identifier, or "unknown".
  return (
    <div className="flex items-baseline justify-between gap-3 py-1" data-testid={testId}>
      <span className="text-sm text-muted-foreground">{label}</span>
      {value ? (
        <span className="flex items-center gap-2 text-sm">
          <span className="font-medium">{value}</span>
          {source && SOURCE_LABEL[source] && (
            <Badge variant="outline" className="text-xs" data-testid={`${testId}-source`}>
              {SOURCE_LABEL[source]}
            </Badge>
          )}
        </span>
      ) : (
        <span className="text-sm italic text-muted-foreground" data-testid={`${testId}-unset`}>
          not set
        </span>
      )}
    </div>
  );
}

export interface ProfilePanelProps {
  profile: UserProfile | undefined;
  effective: UserProfileEffective | undefined;
  isLoading?: boolean;
  /**
   * True when the profile read FAILED. Rendered as an explicit error affordance
   * rather than a blank form + all-"not set" effective view, which would falsely
   * read as an empty-but-loaded profile.
   */
  isError?: boolean;
  /**
   * The read error, surfaced when `isError`. Named `loadError` to stay distinct
   * from the panel's own save-failure state below.
   */
  loadError?: Error | null;
  /** Called with ONLY the fields that changed. */
  onSave: (patch: UserProfilePatch) => Promise<void>;
}

export function ProfilePanel({
  profile,
  effective,
  isLoading,
  isError,
  loadError,
  onSave,
}: ProfilePanelProps) {
  const [draft, setDraft] = useState<Record<ProfileField, string>>(() =>
    emptyDraft(),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Seed the form from the loaded profile ONCE PER DISTINCT SERVER RECORD.
   *
   * Re-seeding on every `profile` reference change discards whatever the user
   * has typed: react-query hands back a new object on each settle, so a
   * background refetch — or simply the first load landing while someone is
   * already typing — silently wiped the form. Keying on the record's identity
   * (`user_id` + `updated_at`) means an in-place refetch of unchanged data
   * leaves edits alone, while a genuine server-side change (notably the
   * post-save read, where the server is authoritative) still re-seeds.
   */
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    if (!profile) return;
    const identity = `${profile.user_id}:${profile.updated_at ?? ''}`;
    if (seededFor.current === identity) return;
    seededFor.current = identity;
    setDraft(draftFrom(profile));
    setError(null);
  }, [profile]);

  const overLimit = useMemo(
    () => FIELDS.filter((f) => runeLength(draft[f.key]) > CAPS[f.key]),
    [draft],
  );

  /**
   * The delta only. A field left untouched is omitted so the server leaves it
   * unchanged; a field cleared to '' is SENT as '' because that is the wire
   * signal to clear it back to unset. Sending the whole form every time would
   * make "unchanged" and "cleared" indistinguishable.
   */
  const patch = useMemo(() => {
    const out: UserProfilePatch = {};
    for (const { key } of FIELDS) {
      const stored = profile?.[key] ?? '';
      if (draft[key] !== stored) out[key] = draft[key];
    }
    return out;
  }, [draft, profile]);

  const dirty = Object.keys(patch).length > 0;

  const handleSave = async () => {
    if (!dirty || overLimit.length > 0) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(patch);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  // A failed read is NOT an empty profile. Seeding a blank form plus an
  // all-"not set" effective view would read as "loaded, but empty"; instead the
  // panel reports the failure explicitly (after all hooks, so hook order holds).
  if (isError) {
    return (
      <div className="flex flex-col gap-6" data-testid="profile-panel">
        <div>
          <h2 className="text-lg font-semibold">Your profile</h2>
          <p className="text-sm text-muted-foreground">
            Facts about you that every agent is given. Blank fields are left out
            entirely — never guessed.
          </p>
        </div>
        <p className="text-sm text-destructive" data-testid="profile-load-error">
          {loadError?.message ?? 'Failed to load your profile.'}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="profile-panel">
      <div>
        <h2 className="text-lg font-semibold">Your profile</h2>
        <p className="text-sm text-muted-foreground">
          Facts about you that every agent is given. Blank fields are left out
          entirely — never guessed.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/*
          The form is withheld until the stored profile has loaded. The patch it
          sends is a DELTA against that baseline, so rendering inputs before the
          baseline is known would compute the delta against empty strings —
          every field would read as "changed", and a save would overwrite values
          the user never saw, let alone edited.
        */}
        {isLoading ? (
          <LoadingState data-testid="profile-loading" label="Loading your profile…" />
        ) : (
        <div className="flex flex-col gap-3">
          {FIELDS.map(({ key, label, placeholder }) => {
            const len = runeLength(draft[key]);
            const over = len > CAPS[key];
            return (
              <div key={key} className="flex flex-col gap-1">
                <Label htmlFor={`profile-${key}`}>{label}</Label>
                {key === 'bio' ? (
                  <textarea
                    id={`profile-${key}`}
                    data-testid={`profile-${key}`}
                    className="min-h-24 rounded-md bg-muted p-3 text-sm"
                    value={draft[key]}
                    placeholder={placeholder}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, [key]: e.target.value }))
                    }
                  />
                ) : (
                  <Input
                    id={`profile-${key}`}
                    data-testid={`profile-${key}`}
                    value={draft[key]}
                    placeholder={placeholder}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, [key]: e.target.value }))
                    }
                  />
                )}
                {over && (
                  <p
                    className="text-xs text-destructive"
                    data-testid={`profile-${key}-error`}
                  >
                    {len}/{CAPS[key]} characters — too long to save.
                  </p>
                )}
              </div>
            );
          })}

          {error && (
            <p className="text-sm text-destructive" data-testid="profile-error">
              {error}
            </p>
          )}

          <div>
            <Button
              data-testid="profile-save"
              disabled={!dirty || overLimit.length > 0 || saving}
              onClick={() => void handleSave()}
            >
              {saving ? 'Saving...' : 'Save profile'}
            </Button>
          </div>
        </div>
        )}

        <div className="flex flex-col gap-1 rounded-md bg-card p-4" data-testid="profile-effective">
          <h3 className="text-sm font-semibold">What the agent is told</h3>
          <p className="mb-2 text-xs text-muted-foreground">
            After precedence. Some values come from somewhere other than this
            form.
          </p>
          <EffectiveRow
            label="Name"
            value={effective?.name}
            source={effective?.name_source}
            testId="effective-name"
          />
          <EffectiveRow label="Pronouns" value={effective?.pronouns} testId="effective-pronouns" />
          <EffectiveRow
            label="Role / title"
            value={effective?.role_title}
            source={effective?.role_title_source}
            testId="effective-role-title"
          />
          <EffectiveRow
            label="Timezone"
            value={effective?.timezone}
            source={effective?.timezone_source}
            testId="effective-timezone"
          />
          <EffectiveRow label="Expertise" value={effective?.expertise} testId="effective-expertise" />
          <EffectiveRow
            label="Working hours"
            value={effective?.working_hours}
            testId="effective-working-hours"
          />
          <EffectiveRow label="About" value={effective?.bio} testId="effective-bio" />
        </div>
      </div>
    </div>
  );
}

function emptyDraft(): Record<ProfileField, string> {
  return {
    preferred_name: '',
    pronouns: '',
    role_title: '',
    timezone: '',
    expertise: '',
    working_hours: '',
    bio: '',
  };
}

function draftFrom(profile: UserProfile | undefined): Record<ProfileField, string> {
  const base = emptyDraft();
  if (!profile) return base;
  for (const { key } of FIELDS) {
    base[key] = profile[key] ?? '';
  }
  return base;
}
